import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, CalendarIcon, XIcon, CheckCircleIcon } from 'lucide-react';
import { ProgressIndicator } from './ProgressIndicator';
interface SlideInviteProps {
  onRestart?: () => void;
}
function EuropeOutline() {
  return <svg viewBox="0 0 300 280" className="w-full h-full" aria-hidden="true" data-id="element-127">
      <defs data-id="element-128">
        <radialGradient id="pinGlow" cx="50%" cy="50%" r="50%" data-id="element-129">
          <stop offset="0%" stopColor="#00B4D8" stopOpacity="0.3" data-id="element-130" />
          <stop offset="100%" stopColor="#00B4D8" stopOpacity="0" data-id="element-131" />
        </radialGradient>
        <radialGradient id="spotlightGlow" cx="50%" cy="40%" r="60%" data-id="element-132">
          <stop offset="0%" stopColor="#00B4D8" stopOpacity="0.06" data-id="element-133" />
          <stop offset="60%" stopColor="#00B4D8" stopOpacity="0.02" data-id="element-134" />
          <stop offset="100%" stopColor="#00B4D8" stopOpacity="0" data-id="element-135" />
        </radialGradient>
      </defs>

      {/* Soft spotlight background */}
      <rect width="300" height="280" fill="url(#spotlightGlow)" data-id="element-136" />

      {/* Abstract network of European cities as dots */}
      {[{
      x: 150,
      y: 80,
      label: 'London',
      delay: 0.3
    }, {
      x: 168,
      y: 108,
      label: 'Paris',
      delay: 0.5,
      isParis: true
    }, {
      x: 200,
      y: 100,
      label: 'Berlin',
      delay: 0.7
    }, {
      x: 180,
      y: 140,
      label: 'Vienna',
      delay: 0.9
    }, {
      x: 160,
      y: 160,
      label: 'Rome',
      delay: 1.1
    }, {
      x: 220,
      y: 120,
      label: 'Warsaw',
      delay: 0.6
    }, {
      x: 130,
      y: 130,
      label: 'Madrid',
      delay: 0.8
    }, {
      x: 240,
      y: 90,
      label: 'Stockholm',
      delay: 0.4
    }].map((city, i) => <g key={i} data-id="element-137">
          <motion.circle cx={city.x} cy={city.y} r={city.isParis ? 3 : 2} fill={city.isParis ? '#00B4D8' : '#94A3B8'} initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: city.isParis ? 1 : 0.6,
        scale: 1
      }} transition={{
        delay: city.delay,
        duration: 0.4
      }} data-id="element-138" />
        </g>)}

      {/* Connection lines */}
      {['M150,80 L168,108', 'M168,108 L200,100', 'M200,100 L220,120', 'M168,108 L180,140', 'M180,140 L160,160', 'M168,108 L130,130', 'M200,100 L240,90'].map((d, i) => <motion.path key={i} d={d} fill="none" stroke="#E2E8F0" strokeWidth="1" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      delay: 0.8 + i * 0.1,
      duration: 0.6
    }} data-id="element-139" />)}

      {/* Paris pin glow */}
      <motion.circle cx="168" cy="108" r="22" fill="url(#pinGlow)" initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: [0, 1.5, 1]
    }} transition={{
      delay: 1.5,
      duration: 1
    }} data-id="element-140" />
      <motion.circle cx="168" cy="108" r="6" fill="#00B4D8" initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 1.8,
      duration: 0.4
    }} data-id="element-141" />
      <motion.circle cx="168" cy="108" r="10" fill="none" stroke="#00B4D8" strokeWidth="1" initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: [0, 0.5, 0],
      scale: [0.5, 2, 3]
    }} transition={{
      delay: 2,
      duration: 2.5,
      repeat: Infinity,
      repeatDelay: 1.5
    }} data-id="element-142" />

      {/* Paris label */}
      <motion.text x="182" y="106" fill="#0891B2" fontSize="8" fontFamily="Inter, sans-serif" fontWeight="600" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2.2,
      duration: 0.6
    }} data-id="element-143">
        Paris
      </motion.text>

      {/* Subtle coordinate text */}
      <motion.text x="182" y="116" fill="#94A3B8" fontSize="6" fontFamily="Inter, sans-serif" initial={{
      opacity: 0
    }} animate={{
      opacity: 0.5
    }} transition={{
      delay: 2.5,
      duration: 0.6
    }} data-id="element-144">
        48.8566° N, 2.3522° E
      </motion.text>
    </svg>;
}
function ConsentForm({
  onClose
}: {
  onClose: () => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  return <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-145">
      <motion.div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative" initial={{
      scale: 0.95,
      y: 20
    }} animate={{
      scale: 1,
      y: 0
    }} exit={{
      scale: 0.95,
      y: 20
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 300
    }} data-id="element-146">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors" aria-label="Close" data-id="element-147">
          <XIcon className="w-5 h-5" data-id="element-148" />
        </button>

        {submitted ? <div className="p-10 text-center flex flex-col items-center" data-id="element-149">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          delay: 0.1
        }} data-id="element-150">
              <CheckCircleIcon className="w-16 h-16 text-teal-500 mb-4" data-id="element-151" />
            </motion.div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-152">
              Interest registered
            </h3>
            <p className="text-slate-500 mb-2" data-id="element-153">
              Thank you for your interest in the Takeda Satellite Symposium.
            </p>
            <p className="text-sm text-slate-400 mb-8" data-id="element-154">
              We'll send event details and confirmation to your email as they
              become available.
            </p>
            <button onClick={onClose} className="w-full py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors" data-id="element-155">
              Return to presentation
            </button>
          </div> : <div className="p-6 sm:p-8" data-id="element-156">
            <h3 className="text-xl font-bold text-slate-900 mb-1" data-id="element-157">
              Register Interest
            </h3>
            <p className="text-sm text-slate-500 mb-6" data-id="element-158">
              Takeda Satellite Symposium · Paris · October 2026
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-159">
              <div className="grid grid-cols-2 gap-4" data-id="element-160">
                <div className="space-y-1" data-id="element-161">
                  <label htmlFor="firstName" className="text-xs font-medium text-slate-700" data-id="element-162">
                    First Name *
                  </label>
                  <input required id="firstName" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-takeda-red focus:border-transparent" data-id="element-163" />
                </div>
                <div className="space-y-1" data-id="element-164">
                  <label htmlFor="lastName" className="text-xs font-medium text-slate-700" data-id="element-165">
                    Last Name *
                  </label>
                  <input required id="lastName" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-takeda-red focus:border-transparent" data-id="element-166" />
                </div>
              </div>

              <div className="space-y-1" data-id="element-167">
                <label htmlFor="email" className="text-xs font-medium text-slate-700" data-id="element-168">
                  Email Address *
                </label>
                <input required id="email" type="email" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-takeda-red focus:border-transparent" data-id="element-169" />
              </div>

              <div className="space-y-1" data-id="element-170">
                <label htmlFor="institution" className="text-xs font-medium text-slate-700" data-id="element-171">
                  Institution / Hospital
                </label>
                <input id="institution" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-takeda-red focus:border-transparent" data-id="element-172" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-173">
                <div className="space-y-1" data-id="element-174">
                  <label htmlFor="country" className="text-xs font-medium text-slate-700" data-id="element-175">
                    Country
                  </label>
                  <select id="country" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-takeda-red focus:border-transparent bg-white" data-id="element-176">
                    <option value="" data-id="element-177">Select...</option>
                    <option value="uk" data-id="element-178">United Kingdom</option>
                    <option value="fr" data-id="element-179">France</option>
                    <option value="de" data-id="element-180">Germany</option>
                    <option value="it" data-id="element-181">Italy</option>
                    <option value="es" data-id="element-182">Spain</option>
                    <option value="nl" data-id="element-183">Netherlands</option>
                    <option value="be" data-id="element-184">Belgium</option>
                    <option value="at" data-id="element-185">Austria</option>
                    <option value="ch" data-id="element-186">Switzerland</option>
                    <option value="pl" data-id="element-187">Poland</option>
                    <option value="se" data-id="element-188">Sweden</option>
                    <option value="other" data-id="element-189">Other</option>
                  </select>
                </div>
                <div className="space-y-1" data-id="element-190">
                  <label htmlFor="specialty" className="text-xs font-medium text-slate-700" data-id="element-191">
                    Specialty
                  </label>
                  <input id="specialty" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-takeda-red focus:border-transparent" data-id="element-192" />
                </div>
              </div>

              <div className="flex items-start gap-3 pt-2" data-id="element-193">
                <input required id="consent" type="checkbox" className="mt-1 w-4 h-4 text-takeda-red border-slate-300 rounded focus:ring-takeda-red" data-id="element-194" />
                <label htmlFor="consent" className="text-xs text-slate-500 leading-tight" data-id="element-195">
                  I consent to Takeda collecting and processing my personal data
                  for the purpose of event registration and related
                  communications.{' '}
                  <a href="#" className="text-takeda-red hover:underline" data-id="element-196">
                    View Privacy Policy
                  </a>
                  . *
                </label>
              </div>

              <button type="submit" className="w-full mt-6 py-3 bg-takeda-red text-white font-semibold rounded-lg hover:bg-takeda-darkred transition-colors focus:outline-none focus:ring-2 focus:ring-takeda-red focus:ring-offset-2" data-id="element-197">
                Register Interest
              </button>
            </form>
          </div>}
      </motion.div>
    </motion.div>;
}
export function SlideInvite({
  onRestart
}: SlideInviteProps) {
  const [showForm, setShowForm] = useState(false);
  return <div className="relative w-full h-full flex flex-col items-center justify-center px-6" data-id="element-198">
      {/* Soft spotlight effect */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/4 w-[600px] h-[600px] rounded-full bg-teal-500/[0.04] blur-3xl pointer-events-none" data-id="element-199" />

      {/* Background map visual */}
      <div className="absolute inset-0 flex items-center justify-center opacity-35 pointer-events-none" data-id="element-200">
        <div className="w-full max-w-lg" data-id="element-201">
          <EuropeOutline data-id="element-202" />
        </div>
      </div>

      <div className="relative z-10 max-w-2xl mx-auto w-full flex flex-col items-center" data-id="element-203">
        {/* Takeda logo */}
        <motion.img src="https://cdn.magicpatterns.com/uploads/eTfkzcG3fiuinch9NVPJME/Screenshot_2026-03-24_at_08.34.05.png" alt="Takeda" className="h-8 mb-8 object-contain" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6
      }} data-id="element-204" />

        <motion.h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 text-center leading-tight mb-4" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.2
      }} data-id="element-205">
          Join the Takeda{' '}
          <span className="text-teal-600" data-id="element-206">Satellite Symposium</span>
        </motion.h2>

        <motion.p className="text-base sm:text-lg text-slate-600 text-center max-w-xl mb-3 leading-relaxed font-medium" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.45
      }} data-id="element-207">
          A scientific discussion on real-world evidence and evolving
          expectations in HAE prophylaxis
        </motion.p>

        <motion.p className="text-sm sm:text-base text-slate-500 text-center max-w-lg mb-8 leading-relaxed" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.65
      }} data-id="element-208">
          Exploring how emerging therapies compare — and what sustained
          real-world control means in clinical practice
        </motion.p>

        {/* Event details card */}
        <motion.div className="w-full max-w-sm bg-white border border-slate-200 rounded-xl p-6 mb-4 shadow-sm" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.85
      }} data-id="element-209">
          <div className="flex items-center gap-4" data-id="element-210">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0" data-id="element-211">
              <MapPinIcon className="w-5 h-5 text-teal-600" aria-hidden="true" data-id="element-212" />
            </div>
            <div data-id="element-213">
              <p className="text-base font-semibold text-slate-900" data-id="element-214">
                Paris, France
              </p>
              <p className="text-sm text-slate-500" data-id="element-215">Europe</p>
            </div>
          </div>

          <div className="w-full h-px bg-slate-100 my-4" data-id="element-216" />

          <div className="flex items-center gap-4" data-id="element-217">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0" data-id="element-218">
              <CalendarIcon className="w-5 h-5 text-teal-600" aria-hidden="true" data-id="element-219" />
            </div>
            <div data-id="element-220">
              <p className="text-base font-semibold text-slate-900" data-id="element-221">
                14–17 October 2026
              </p>
              <p className="text-sm text-slate-500" data-id="element-222">Details to follow</p>
            </div>
          </div>
        </motion.div>

        {/* By invitation micro-line */}
        <motion.p className="text-xs font-medium tracking-widest uppercase text-teal-600/70 mb-8" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.0,
        duration: 0.6
      }} data-id="element-223">
          By invitation
        </motion.p>

        <motion.button onClick={() => setShowForm(true)} className="px-10 py-4 bg-takeda-red text-white font-bold rounded-lg text-lg
                     hover:bg-takeda-darkred transition-all duration-300
                     shadow-xl shadow-red-500/20 
                     focus:outline-none focus:ring-2 focus:ring-takeda-red focus:ring-offset-2 focus:ring-offset-white" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 1.15
      }} whileHover={{
        scale: 1.03
      }} whileTap={{
        scale: 0.98
      }} aria-label="Register interest for the Takeda Satellite Symposium" data-id="element-224">
          Register interest
        </motion.button>

        <motion.div className="mt-8" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.4,
        duration: 0.6
      }} data-id="element-225">
          <ProgressIndicator currentStep={7} totalSteps={7} data-id="element-226" />
        </motion.div>

        {onRestart && <motion.button onClick={onRestart} className="mt-5 text-sm text-slate-400 hover:text-slate-600 transition-colors underline underline-offset-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 2,
        duration: 0.6
      }} data-id="element-227">
            Restart experience
          </motion.button>}
      </div>

      {/* Consent Form Modal */}
      <AnimatePresence data-id="element-228">
        {showForm && <ConsentForm onClose={() => setShowForm(false)} data-id="element-229" />}
      </AnimatePresence>
    </div>;
}